import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUSES,
  TICKET_STATUS_LABELS,
} from '../shared/index.js';

import type { BoardFilters, BoardViewMode } from '../app/filters.js';
import { BOARD_VIEW_MODES, EMPTY_FILTERS, UNASSIGNED, activeFilterCount } from '../app/filters.js';
import type { BoardCard } from '../api/endpoints.js';
import { useDepartments, useDirectory, useProjects } from '../hooks/use-directory.js';
import { useSession } from '../auth/session.js';
import type { SelectOption } from './select.js';
import { MultiSelect } from './select.js';

interface FilterBarProps {
  readonly filters: BoardFilters;
  readonly onChange: (next: BoardFilters) => void;
  readonly view: BoardViewMode;
  readonly onViewChange: (next: BoardViewMode) => void;
  readonly tickets: readonly BoardCard[];
  readonly total: number;
  readonly shown: number;
}

export function FilterBar({
  filters,
  onChange,
  view,
  onViewChange,
  tickets,
  total,
  shown,
}: FilterBarProps): ReactNode {
  const { user } = useSession();
  const { departments } = useDepartments();
  const projects = useProjects();
  const people = useDirectory();
  const [expanded, setExpanded] = useState(false);

  const patch = (part: Partial<BoardFilters>): void => {
    onChange({ ...filters, ...part });
  };

  const count = activeFilterCount(filters);

  const present = useMemo(
    () => ({
      departmentIds: new Set(tickets.map((ticket) => ticket.departmentId)),
      projectIds: new Set(
        tickets.flatMap((ticket) => (ticket.projectId === null ? [] : [ticket.projectId])),
      ),
      assigneeIds: new Set(
        tickets.flatMap((ticket) => (ticket.assigneeId === null ? [] : [ticket.assigneeId])),
      ),
      hasUnassigned: tickets.some((ticket) => ticket.assigneeId === null),
    }),
    [tickets],
  );

  const assigneeOptions: readonly SelectOption[] = useMemo(
    () => [
      ...(user === null ? [] : [{ value: user.id, label: 'Me' }]),
      ...(present.hasUnassigned ? [{ value: UNASSIGNED, label: 'Unassigned' }] : []),
      ...people
        .filter(
          (person) =>
            person.id !== user?.id &&
            (present.assigneeIds.has(person.id) || filters.assigneeIds.includes(person.id)),
        )
        .map((person) => ({
          value: person.id,
          label: `${person.firstName} ${person.lastName}`,
        })),
    ],
    [people, user, present, filters.assigneeIds],
  );

  const departmentOptions: readonly SelectOption[] = useMemo(
    () => [
      ...departments
        .filter(
          (department) =>
            present.departmentIds.has(department.id) ||
            filters.departmentIds.includes(department.id),
        )
        .map((department) => ({ value: department.id, label: department.name })),
    ],
    [departments, present, filters.departmentIds],
  );

  const projectOptions: readonly SelectOption[] = useMemo(
    () => [
      ...projects
        .filter(
          (project) =>
            present.projectIds.has(project.id) || filters.projectIds.includes(project.id),
        )
        .map((project) => ({ value: project.id, label: project.name })),
    ],
    [projects, present, filters.projectIds],
  );

  const priorityOptions: readonly SelectOption[] = useMemo(
    () => [
      ...TICKET_PRIORITIES.map((priority) => ({
        value: priority,
        label: TICKET_PRIORITY_LABELS[priority],
      })),
    ],
    [],
  );

  const statusOptions: readonly SelectOption[] = useMemo(
    () => [
      ...TICKET_STATUSES.map((status) => ({ value: status, label: TICKET_STATUS_LABELS[status] })),
    ],
    [],
  );

  const worthShowing = (options: readonly SelectOption[], active: boolean): boolean =>
    active || options.length > 2;

  return (
    <div className="border-b border-hairline bg-white/70 px-4 py-3 backdrop-blur sm:px-7 dark:border-dark-line dark:bg-dark-panel/70">
      <div className="flex items-center gap-2">
        <input
          type="search"
          value={filters.search}
          onChange={(event) => {
            patch({ search: event.target.value });
          }}
          placeholder="Search"
          aria-label="Search requests"
          className="h-11 min-w-0 flex-1 rounded-field border border-[rgb(223,228,235)] bg-white px-3.5 text-sm font-normal text-[rgb(20,32,51)] outline-none transition placeholder:text-muted/70 focus:border-royal/55 focus:ring-[3px] focus:ring-royal/15 md:max-w-72 dark:border-dark-line dark:bg-dark-field dark:text-dark-ink"
        />

        <button
          type="button"
          onClick={() => {
            setExpanded((current) => !current);
          }}
          aria-expanded={expanded}
          className={`flex h-9 shrink-0 items-center gap-1.5 rounded-field border px-3 text-[13px] font-semibold transition md:hidden ${
            count > 0
              ? 'border-royal/40 bg-royal/8 text-royal'
              : 'border-hairline bg-white text-navy dark:border-dark-line dark:bg-dark-field dark:text-dark-ink'
          }`}
        >
          Filters
          {count > 0 ? (
            <span className="grid size-4 place-items-center rounded-full bg-royal text-[10px] font-bold text-white">
              {count}
            </span>
          ) : null}
        </button>

        <span className="ml-auto flex shrink-0 items-center gap-3">
          <span className="hidden text-[12.5px] font-semibold text-muted sm:inline">
            {shown === total
              ? `${String(total)} ${total === 1 ? 'request' : 'requests'}`
              : `${String(shown)} of ${String(total)}`}
          </span>

          <span className="flex rounded-field border border-hairline bg-white p-0.5 dark:border-dark-line dark:bg-dark-field">
            {BOARD_VIEW_MODES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onViewChange(option);
                }}
                className={`rounded-[9px] px-3 py-1 text-[12.5px] font-bold capitalize transition ${
                  view === option
                    ? 'bg-rail-active text-white'
                    : 'text-muted hover:text-navy dark:hover:text-dark-ink'
                }`}
              >
                {option === 'archived' ? 'archive' : option}
              </button>
            ))}
          </span>
        </span>
      </div>

      <div className={`mt-3 flex-wrap items-center gap-2 md:flex ${expanded ? 'flex' : 'hidden'}`}>
        {worthShowing(assigneeOptions, filters.assigneeIds.length > 0) ? (
          <MultiSelect
            label="Assignee"
            placeholder="Anyone"
            value={filters.assigneeIds}
            options={assigneeOptions}
            onChange={(next) => {
              patch({ assigneeIds: next });
            }}
          />
        ) : null}

        {worthShowing(departmentOptions, filters.departmentIds.length > 0) ? (
          <MultiSelect
            label="Department"
            placeholder="Any department"
            value={filters.departmentIds}
            options={departmentOptions}
            onChange={(next) => {
              patch({ departmentIds: next });
            }}
          />
        ) : null}

        {worthShowing(projectOptions, filters.projectIds.length > 0) ? (
          <MultiSelect
            label="Project"
            placeholder="Any project"
            value={filters.projectIds}
            options={projectOptions}
            onChange={(next) => {
              patch({ projectIds: next });
            }}
          />
        ) : null}

        <MultiSelect
          label="Priority"
          placeholder="Any priority"
          value={filters.priorities}
          options={priorityOptions}
          onChange={(next) => {
            patch({ priorities: next as BoardFilters['priorities'] });
          }}
        />

        <MultiSelect
          label="Status"
          placeholder="Any status"
          value={filters.statuses}
          options={statusOptions}
          onChange={(next) => {
            patch({ statuses: next as BoardFilters['statuses'] });
          }}
        />

        {count > 0 ? (
          <button
            type="button"
            onClick={() => {
              onChange(EMPTY_FILTERS);
            }}
            className="h-9 rounded-field px-3 text-[13px] font-semibold text-royal hover:bg-royal/8"
          >
            Clear {count === 1 ? 'filter' : `${String(count)} filters`}
          </button>
        ) : null}
      </div>
    </div>
  );
}
